import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Spinner } from '@structural-climbing/ui'
import { useGymThemeContext } from '../../layouts/useGymTheme'
import { MemberIdentity } from '../members/MemberIdentity'

interface Attendee {
  user_id: string
  name: string
  nickname: string | null
  email: string | null
  avatar_ext: string | null
  avatar_updated_at: string | null
}

// Who joined an event, expanded inline under the news row as member rows
// (avatar + name); tapping a row opens the full member profile.
export function AttendeesList({
  newsId,
  onSelect,
}: {
  newsId: string
  onSelect: (userId: string) => void
}) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const { primaryColor } = useGymThemeContext()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [attendees, setAttendees] = useState<Attendee[] | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${apiUrl}/news/${newsId}/signups`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) throw new Error()
        const data = (await res.json()) as Attendee[]
        if (!cancelled) setAttendees(data)
      } catch {
        if (!cancelled) setAttendees([])
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [newsId, apiUrl, getAccessTokenSilently])

  if (attendees === null) {
    return (
      <div className="flex justify-center py-4">
        <Spinner size="sm" color="primary" />
      </div>
    )
  }

  if (attendees.length === 0) {
    return <p className="py-2 text-center text-sm text-foreground/50">{t('news.attendeesEmpty')}</p>
  }

  return (
    <ul className="flex flex-col gap-1">
      {attendees.map((a) => (
        <li key={a.user_id}>
          <button
            type="button"
            className="flex w-full items-center rounded-md px-2 py-2 text-left transition-colors hover:bg-content2"
            onClick={() => onSelect(a.user_id)}
            title={t('members.viewProfile')}
          >
            <MemberIdentity
              userId={a.user_id}
              name={a.name}
              nickname={a.nickname}
              email={a.email}
              avatarExt={a.avatar_ext}
              avatarUpdatedAt={a.avatar_updated_at}
              primaryColor={primaryColor}
              size="sm"
            />
          </button>
        </li>
      ))}
    </ul>
  )
}
